import React, { useEffect, useState } from 'react';
import {
  PeekButton,
  PeekEdit,
  PeekHead,
  PeekHeading,
  PeekItem,
  PeekItemHead,
  PeekLabel,
  PeekList,
  PeekMarks,
  PeekNote,
  PeekPanel,
  PeekScrim,
  PeekText,
  PeekWrap,
} from './style';

export const marksText = (marks) =>
  `${marks ?? 0} ${Number(marks) === 1 ? 'mark' : 'marks'}`;

/** Plain text of a question's HTML (the peek shows text, never markup). */
export const plainText = (html) => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  } catch {
    return String(html)
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
};

const byOrder = (a, b) => (a?.order ?? 0) - (b?.order ?? 0);

/** Questions in reading order: each top-level question, then its parts. */
export const orderedQuestions = (questions) => {
  const list = Array.isArray(questions) ? questions.filter(Boolean) : [];
  const tops = list.filter((question) => !question.parentId).sort(byOrder);
  return tops.flatMap((top, index) => [
    { question: top, depth: 0, label: top.label || `Q${index + 1}` },
    ...list
      .filter((question) => question.parentId === top.questionId)
      .sort(byOrder)
      .map((part, partIndex) => ({
        question: part,
        depth: 1,
        label:
          part.label ||
          `${top.label || `Q${index + 1}`}${String.fromCharCode(
            97 + partIndex
          )}`,
      })),
  ]);
};

const hasStimulus = (question) =>
  Boolean(
    question?.stimulus &&
      ((question.stimulus.text && plainText(question.stimulus.text)) ||
        question.stimulus.assets?.length ||
        question.stimulus.assetIds?.length)
  );

const totalOf = (exam, rows) =>
  exam?.totalMarks ??
  rows
    .filter(({ question }) =>
      rows.every((row) => row.question.parentId !== question.questionId)
    )
    .reduce((sum, { question }) => sum + (Number(question.marks) || 0), 0);

const QuestionIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/**
 * "View question(s)" (spec §6 [C], tplA 422–431): a peek at ALL questions.
 * Hidden while the exam has no questions. "Edit in Questions" appears when
 * the teacher is elsewhere and Questions is editable.
 */
export default function ViewQuestionsPeek({ exam, canEdit, onEdit }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const rows = orderedQuestions(exam?.questions);
  if (rows.length === 0) return null;
  const single = rows.length === 1;
  const label = single ? 'View question' : 'View questions';
  return (
    <PeekWrap>
      <PeekButton
        type="button"
        title={label}
        aria-label={label}
        aria-expanded={open}
        $open={open}
        onClick={() => setOpen((value) => !value)}
      >
        <QuestionIcon />
      </PeekButton>
      {open && (
        <>
          <PeekScrim onClick={() => setOpen(false)} />
          <PeekPanel role="dialog" aria-label={label}>
            <PeekHead>
              <PeekHeading>{single ? 'Question' : 'Questions'}</PeekHeading>
              <PeekMarks>{marksText(totalOf(exam, rows))}</PeekMarks>
            </PeekHead>
            <PeekList>
              {rows.map(({ question, depth, label: qLabel }) => (
                <PeekItem key={question.questionId} $depth={depth}>
                  {!single && (
                    <PeekItemHead>
                      <PeekLabel>{qLabel}</PeekLabel>
                      <PeekMarks>{marksText(question.marks)}</PeekMarks>
                    </PeekItemHead>
                  )}
                  {plainText(question.text) && (
                    <PeekText>{plainText(question.text)}</PeekText>
                  )}
                  {hasStimulus(question) && (
                    <PeekNote>Includes stimulus</PeekNote>
                  )}
                </PeekItem>
              ))}
            </PeekList>
            {canEdit && (
              <PeekEdit
                type="button"
                onClick={() => {
                  setOpen(false);
                  onEdit?.();
                }}
              >
                Edit in Questions
              </PeekEdit>
            )}
          </PeekPanel>
        </>
      )}
    </PeekWrap>
  );
}
